import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BillingSuccessPage() {
  const [searchParams] = useSearchParams();
  const { token, refreshOrganization } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [pollCount, setPollCount] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (pollCount >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/billing/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setStatus('success');
        await refreshOrganization();
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      // Continue polling
      setPollCount(prev => prev + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Payment status error:', error);
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-[#18181b] border border-white/10 rounded-2xl p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-emerald-500 mx-auto mb-6 animate-spin" />
            <h1 className="font-heading text-2xl text-white uppercase mb-2">Processing Payment</h1>
            <p className="text-gray-400">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl text-white uppercase mb-2">Payment Successful!</h1>
            <p className="text-gray-400 mb-6">Your plan has been upgraded. Thank you for your purchase!</p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white btn-lift"
              data-testid="go-dashboard-btn"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-6" />
            <h1 className="font-heading text-2xl text-white uppercase mb-2">
              {status === 'timeout' ? 'Verification Timeout' : 'Payment Issue'}
            </h1>
            <p className="text-gray-400 mb-6">
              {status === 'timeout' 
                ? 'Payment verification is taking longer than expected. Your payment may still be processing.'
                : 'There was an issue with your payment. Please try again.'}
            </p>
            <div className="flex gap-3">
              <Button
                onClick={() => navigate('/billing')}
                variant="outline"
                className="flex-1 border-white/10 text-white hover:bg-white/5"
              >
                Back to Billing
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
